/**
 * Admin Authentication Service
 * Handles PostgreSQL role-based authentication for admin users via Edge Functions
 * Separate from Supabase authentication for enhanced security
 */

import { logger } from '@/utils/logger';

export interface AdminUser {
  user_id: string;
  db_username: string;
  role: 'admin' | 'moderator' | 'support';
  email: string;
  full_name: string;
}

export interface AdminSession {
  session_token: string;
  user_id: string;
  role: string;
  expires_at: string;
}

export interface AdminLoginCredentials {
  username: string;
  password: string;
  ip_address?: string;
  user_agent?: string;
}

export interface AdminLoginResult {
  success: boolean;
  user?: AdminUser;
  session?: AdminSession;
  error?: string;
}

export class AdminAuthService {
  private static instance: AdminAuthService;
  private currentSession: AdminSession | null = null;
  private readonly baseUrl: string;

  private constructor() {
    // Use AWS API Gateway for admin authentication
    this.baseUrl = import.meta.env.VITE_ADMIN_AUTH_API_URL || 'https://api.iwishbag.com/admin-auth';
  }

  static getInstance(): AdminAuthService {
    if (!AdminAuthService.instance) {
      AdminAuthService.instance = new AdminAuthService();
    }
    return AdminAuthService.instance;
  }

  private getApiUrl(endpoint: string): string {
    return `${this.baseUrl}${endpoint}`;
  }

  /**
   * Admin login using database credentials via AWS Lambda
   */
  async login(credentials: AdminLoginCredentials): Promise<AdminLoginResult> {
    try {
      const { username, password, ip_address, user_agent } = credentials;

      // Validate credentials format
      if (!username || !password) {
        return { success: false, error: 'Username and password are required' };
      }

      // Make HTTP request to AWS Lambda via API Gateway
      const response = await fetch(this.getApiUrl('/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username,
          password,
          ip_address,
          user_agent
        })
      });

      const result = await response.json();

      if (!response.ok) {
        logger.warn('Admin login failed', { username, ip_address, status: response.status });
        return { success: false, error: result.error || 'Login failed' };
      }

      if (result.success && result.user && result.session) {
        // Store session locally
        this.currentSession = result.session;

        logger.info('Admin login successful', {
          user_id: result.user.user_id,
          role: result.user.role,
          ip_address
        });

        return {
          success: true,
          user: result.user,
          session: result.session
        };
      } else {
        return { success: false, error: result.error || 'Login failed' };
      }

    } catch (error) {
      logger.error('Admin login error', { error });
      return { success: false, error: 'Authentication system error' };
    }
  }

  /**
   * Validate admin session via AWS Lambda
   */
  async validateSession(sessionToken: string): Promise<AdminUser | null> {
    try {
      const response = await fetch(this.getApiUrl('/validate'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_token: sessionToken
        })
      });

      const result = await response.json();

      if (!response.ok || !result.valid) {
        return null;
      }

      return result.user;

    } catch (error) {
      logger.error('Session validation error', { error });
      return null;
    }
  }

  /**
   * Admin logout via AWS Lambda
   */
  async logout(sessionToken?: string): Promise<boolean> {
    try {
      const token = sessionToken || this.currentSession?.session_token;
      if (!token) {
        return false;
      }

      const response = await fetch(this.getApiUrl('/logout'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_token: token
        })
      });

      const result = await response.json();
      const success = result.success || false;

      if (success) {
        this.currentSession = null;
        logger.info('Admin logout successful');
      }

      return success;

    } catch (error) {
      logger.error('Admin logout error', { error });
      return false;
    }
  }

  /**
   * Get current session
   */
  getCurrentSession(): AdminSession | null {
    return this.currentSession;
  }

  /**
   * Check if current session is valid
   */
  async isAuthenticated(): Promise<boolean> {
    if (!this.currentSession) {
      return false;
    }

    const user = await this.validateSession(this.currentSession.session_token);
    return user !== null;
  }

  /**
   * Get current user
   */
  async getCurrentUser(): Promise<AdminUser | null> {
    if (!this.currentSession) {
      return null;
    }

    return await this.validateSession(this.currentSession.session_token);
  }

  /**
   * Check if current user has specific role
   */
  async hasRole(role: 'admin' | 'moderator' | 'support'): Promise<boolean> {
    const user = await this.getCurrentUser();
    return user?.role === role;
  }

  /**
   * Check if current user has admin privileges
   */
  async isAdmin(): Promise<boolean> {
    return await this.hasRole('admin');
  }

  /**
   * Cleanup expired sessions (admin function) - Not implemented in current AWS Lambda
   */
  async cleanupExpiredSessions(): Promise<number> {
    logger.info('Session cleanup is handled automatically by PostgreSQL triggers');
    return 0;
  }

  /**
   * Get active admin sessions (admin only) via AWS Lambda
   */
  async getActiveSessions(): Promise<any[]> {
    try {
      const response = await fetch(this.getApiUrl('/sessions'), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const result = await response.json();

      if (!response.ok) {
        logger.error('Failed to get active sessions', { error: result.error });
        return [];
      }

      return result.sessions || [];

    } catch (error) {
      logger.error('Failed to get active sessions', { error });
      return [];
    }
  }

  /**
   * Force logout session (admin only) - Not implemented in current AWS Lambda
   */
  async forceLogout(sessionId: string): Promise<boolean> {
    logger.warn('Force logout not implemented in current AWS Lambda version');
    return false;
  }
}

export const adminAuth = AdminAuthService.getInstance();